import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { check } from "../Can";
import { menuGroupsConfig } from "../../utils/menuGroups";

const flag = (key, whenMissing) => {
  try {
    const value = localStorage.getItem(key);
    if (value == null) return whenMissing;
    return value === "true";
  } catch (_err) {
    return whenMissing;
  }
};

const isSuper = user => Boolean(
  user && (user.profile === "super" || user.super === true || user.super === 1 || String(user.super) === "1")
);

const isElevated = user => Boolean(
  user && (user.profile === "admin" || user.profile === "super" || isSuper(user))
);

const allows = (conditional, user) => {
  switch (conditional) {
    case "showKanban": return flag("showKanban", false);
    case "showSchedules": return flag("showSchedules", false);
    case "showInternalChat": return flag("showInternalChat", false);
    case "showCampaigns": return flag("showCampaigns", false);
    case "showOpenAi": return flag("showOpenAi", false);
    case "showIntegrations": return flag("showIntegrations", false);
    case "showExternalApi": return flag("showExternalApi", false);
    case "showFiles": return flag("showFiles", false);
    case "showGroupCampaigns": return flag("showGroupCampaigns", true);
    case "showStatusCampaigns": return flag("showStatusCampaigns", true);
    case "showNegocio": return isSuper(user) || (user?.profile === "admin" && flag("showNegocio", false));
    case "canAccessAgenda": return flag("canAccessAgenda", false);
    case "user.super": return isSuper(user);
    case "user.superOrCompanyAdmin": return isElevated(user);
    default: return true;
  }
};

const groupAllowed = (group, user) => {
  if (group.conditional && !allows(group.conditional, user)) return false;
  const adminOnly = group.id !== "whatsapp" && group.id !== "dashboard";
  if (adminOnly && !isElevated(user)) return false;
  if (group.id === "dashboard" && !check(user?.profile, "dashboard:view")) return false;
  return true;
};

const itemAllowed = (item, user) => {
  if (!item?.route || item.kind === "section") return false;
  if (item.conditional && !allows(item.conditional, user)) return false;
  return check(user?.profile, item.permission);
};

const routeScore = (route, pathname) => {
  if (!route || route === "/") return pathname === "/" ? 1 : 0;
  if (pathname === route) return route.length + 1;
  if (pathname.startsWith(`${route}/`)) return route.length;
  return 0;
};

export const mobileBarItems = (user, pathname) => {
  const groups = Object.values(menuGroupsConfig).filter(group => groupAllowed(group, user));
  let current = null;
  let best = 0;
  groups.forEach(group => {
    (group.items || []).forEach(item => {
      if (!itemAllowed(item, user)) return;
      const score = routeScore(item.route, pathname);
      if (score > best) {
        best = score;
        current = group;
      }
    });
  });
  if (!current) current = groups.find(group => group.id === "whatsapp") || groups[0];
  if (!current) return [];
  return (current.items || []).filter(item => itemAllowed(item, user)).map(item => ({
    id: item.route,
    to: item.route,
    label: i18n.t(item.translationKey),
    icon: item.icon,
    active: path => item.exact
      ? path === item.route
      : routeScore(item.route, path) > 0,
  }));
};

export const hideMobileBar = pathname => /^\/tickets\/.+/.test(pathname) || /^\/chats\/.+/.test(pathname);

const useStyles = makeStyles(theme => ({
  bar: {
    display: "flex",
    position: "fixed",
    left: 12,
    right: 12,
    bottom: "calc(10px + env(safe-area-inset-bottom, 0px))",
    zIndex: theme.zIndex.appBar - 1,
    alignItems: "stretch",
    gap: 2,
    minHeight: 68,
    padding: "10px 8px 8px",
    borderRadius: 24,
    overflowX: "auto",
    backgroundColor: theme.mode === "dark" ? theme.palette.background.paper : "#fff",
    boxShadow: theme.mode === "dark"
      ? "0 10px 28px rgba(0,0,0,.45)"
      : "0 8px 24px rgba(15, 23, 42, .12)",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  spread: {
    justifyContent: "space-around",
  },
  link: {
    display: "flex",
    flex: "0 0 72px",
    minWidth: 64,
    maxWidth: 92,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: "2px 2px 0",
    borderRadius: 16,
    color: "#9aa0b4",
    textDecoration: "none",
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.1,
    "& svg": { fontSize: 22 },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  active: {
    color: theme.palette.primary.main,
  },
  label: {
    position: "relative",
    maxWidth: "100%",
    overflow: "hidden",
    textAlign: "center",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    paddingBottom: 5,
  },
  labelOn: {
    "&:after": {
      content: '""',
      position: "absolute",
      left: "50%",
      bottom: 0,
      width: 16,
      height: 3,
      marginLeft: -8,
      borderRadius: 99,
      backgroundColor: "currentColor",
    },
  },
}));

export default function MobileBottomNav() {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { pathname } = useLocation();
  const { user } = useContext(AuthContext);
  const barRef = useRef(null);
  const [lang, setLang] = useState(i18n.language);
  useEffect(() => {
    const refresh = next => setLang(next);
    i18n.on("languageChanged", refresh);
    return () => i18n.off("languageChanged", refresh);
  }, []);
  const items = useMemo(() => mobileBarItems(user, pathname), [user, pathname, lang]);
  useEffect(() => {
    const current = barRef.current?.querySelector("[aria-current='page']");
    if (current?.scrollIntoView) current.scrollIntoView({ inline: "center", block: "nearest" });
  }, [pathname, items.length]);
  useEffect(() => {
    const el = barRef.current;
    if (!el) {
      document.documentElement.style.removeProperty("--mobile-bar-space");
      return undefined;
    }
    const sync = () => {
      const space = Math.ceil(window.innerHeight - el.getBoundingClientRect().top);
      if (space > 0) document.documentElement.style.setProperty("--mobile-bar-space", `${space}px`);
    };
    sync();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync);
    if (observer) observer.observe(el);
    window.addEventListener("resize", sync);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", sync);
      document.documentElement.style.removeProperty("--mobile-bar-space");
    };
  }, [pathname, items.length, mobile]);
  if (!mobile || hideMobileBar(pathname) || !items.length) return null;
  return (
    <nav
      ref={barRef}
      className={`${classes.bar}${items.length <= 5 ? ` ${classes.spread}` : ""}`}
      aria-label={i18n.t("mainDrawer.mobileBar.label")}
    >
      {items.map(item => {
        const Icon = item.icon;
        const current = item.active(pathname);
        return (
          <Link key={item.id} to={item.to} className={`${classes.link}${current ? ` ${classes.active}` : ""}`} aria-current={current ? "page" : undefined}>
            <Icon />
            <span className={`${classes.label}${current ? ` ${classes.labelOn}` : ""}`}>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
